import React, { useState } from 'react';
import { Alert, Card, Space, Switch, Table, Tag, Typography } from 'antd';
import { FieldTimeOutlined } from '@ant-design/icons';
import moment from 'moment';
import { Link } from 'react-router-dom';

import { useCronControls, setCronEnabled } from '../../../models/useCronControls';
import {
  KNOWN_CRONS, deriveCronHealth, effectiveEnabled, formatTs, mergeWithCatalog,
} from '../../../utils/cronControls';

const { Text } = Typography;

// Re-exported so existing imports/tests keep one source of truth (utils/cronControls).
export {
  KNOWN_CRONS, deriveCronHealth, effectiveEnabled, mergeWithCatalog,
} from '../../../utils/cronControls';

/**
 * Ops → Crons: every registered cron with its on/off switch. All data comes
 * through the useCronControls model (live subscription; ops/** is
 * admin-read-only by rules) and all writes go through the setCronEnabled
 * action — this screen renders and wires, nothing else. Each row links to
 * /admin/ops/crons/:cronName for the full status page + switch history.
 */
export function AdminCronsScreen() {
  const [loading, controls, loadError] = useCronControls();
  const [toggleError, setToggleError] = useState(null);
  const [togglingName, setTogglingName] = useState(null);

  const rows = mergeWithCatalog(KNOWN_CRONS, controls);

  const onToggle = async (row, enabled) => {
    setTogglingName(row.name);
    setToggleError(null);
    try {
      await setCronEnabled(row.name, enabled);
      // No local state write — the subscription delivers the new truth.
    } catch (err) {
      setToggleError(err.message || `Could not switch ${row.name}`);
    } finally {
      setTogglingName(null);
    }
  };

  const columns = [
    {
      title: 'Cron',
      dataIndex: 'name',
      key: 'name',
      render: (name, row) => (
        <Space direction="vertical" size={0}>
          <Link to={`/admin/ops/crons/${name}`} data-testid={`cron-link-${name}`}>
            <Text code>{name}</Text>
          </Link>
          {row.description && <Text type="secondary">{row.description}</Text>}
        </Space>
      ),
    },
    {
      title: 'Schedule',
      dataIndex: 'schedule',
      key: 'schedule',
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Runs',
      key: 'enabled',
      render: (_, row) => {
        const on = effectiveEnabled(row);
        return (
          <Space>
            <Switch
              checked={on}
              loading={togglingName === row.name}
              onChange={(next) => onToggle(row, next)}
              data-testid={`cron-switch-${row.name}`}
            />
            {on
              ? <Tag color="green" data-testid={`cron-on-${row.name}`}>ON</Tag>
              : <Tag color="red" data-testid={`cron-off-${row.name}`}>OFF</Tag>}
            {row.enabled == null && <Tag data-testid={`cron-default-${row.name}`}>default</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Health',
      key: 'health',
      render: (_, row) => {
        const health = deriveCronHealth(row, moment().valueOf());
        if (health.status === 'unknown') {
          return <Tag data-testid={`cron-health-unknown-${row.name}`}>no ticks yet</Tag>;
        }
        if (health.status === 'overdue') {
          return (
            <Tag color="volcano" data-testid={`cron-health-overdue-${row.name}`}>
              OVERDUE — silent since {moment(health.sinceMs).format('YYYY-MM-DD HH:mm')}
            </Tag>
          );
        }
        if (health.status === 'failing') {
          return (
            <Space direction="vertical" size={0}>
              <Tag color="red" data-testid={`cron-health-failing-${row.name}`}>FAILING</Tag>
              {health.lastError && <Text type="secondary">{health.lastError}</Text>}
            </Space>
          );
        }
        return <Tag color="green" data-testid={`cron-health-healthy-${row.name}`}>healthy</Tag>;
      },
    },
    {
      title: 'Last activity',
      key: 'activity',
      render: (_, row) => {
        const ran = formatTs(row.lastRunAt);
        const skipped = formatTs(row.lastSkippedAt);
        if (!ran && !skipped) return <Text type="secondary">no ticks yet</Text>;
        return (
          <Space direction="vertical" size={0}>
            {ran && <Text type="secondary">ran {ran}</Text>}
            {skipped && <Text type="secondary">skipped {skipped} (was off)</Text>}
          </Space>
        );
      },
    },
    {
      title: 'Last changed',
      key: 'changed',
      render: (_, row) => (row.updatedBy
        ? (
          <Space direction="vertical" size={0}>
            <a href={`/admin/users/${row.updatedBy}`}>{row.updatedByName || <Text code>{row.updatedBy}</Text>}</a>
            {formatTs(row.updatedAt) && <Text type="secondary">{formatTs(row.updatedAt)}</Text>}
          </Space>
        )
        : <Text type="secondary">never — running on its default</Text>),
    },
  ];

  return (
    <Card
      style={{ textAlign: 'left' }}
      title={<Text strong><FieldTimeOutlined /> Crons</Text>}
      data-testid="admin-crons-screen"
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Text type="secondary">
          Every backend cron checks its switch here before doing any work — OFF means
          the schedule still fires but the tick exits immediately (and records the
          skip). Changes take effect on the next tick; no deploy involved. Click a
          cron for its full status page and switch history.
        </Text>
        {loadError && <Alert type="error" showIcon message={loadError} style={{ borderRadius: 8 }} />}
        {toggleError && (
          <Alert
            type="error"
            showIcon
            closable
            onClose={() => setToggleError(null)}
            message={toggleError}
            style={{ borderRadius: 8 }}
            data-testid="cron-toggle-error"
          />
        )}
        <Table
          rowKey="name"
          size="small"
          loading={loading}
          pagination={false}
          columns={columns}
          dataSource={rows}
        />
      </Space>
    </Card>
  );
}

export default AdminCronsScreen;
